import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import Spinner from '../../components/ui/Spinner';
import MessageBox from '../../components/ui/MessageBox';
import Button from '../../components/ui/Button';
import html2canvas from 'html2canvas';
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import { jsPDF } from "jspdf";
import { PlusCircle, FileEdit, XCircle, Printer, Send } from "lucide-react";

const normalizeInvoiceHtml = (html = '') =>
  html.replace(/src\s*=\s*(["'])[^"']*\/images\/SrbLogo\.jpeg\1/gi, 'src=$1/images/SrbLogo.jpeg$1');

const waitForImages = async (element) => {
  const images = Array.from(element?.querySelectorAll('img') || []);
  await Promise.all(images.map((img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });
  }));
};

const copyImageToClipboard = async (blob) => {
  if (!blob || !navigator.clipboard || !window.ClipboardItem) return false;

  try {
    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
    return true;
  } catch (err) {
    console.warn('Clipboard image copy failed:', err);
    return false;
  }
};

const shareImageFile = async (blob) => {
  if (!blob || !navigator.share || !navigator.canShare) return false;

  const file = new File([blob], 'invoice.png', { type: 'image/png' });
  if (!navigator.canShare({ files: [file] })) return false;

  try {
    await navigator.share({
      files: [file],
      title: 'Invoice',
      text: 'Please find attached invoice.',
    });
    return true;
  } catch (err) {
    console.warn('Native image share failed:', err);
    return false;
  }
};

const PrintPreview = ({ htmlContent, onClose, order }) => {
  const hiddenRef = React.useRef(null);
  const printableHtml = normalizeInvoiceHtml(htmlContent);
  const getCaptureElement = () => {
  const doc =
    document.getElementById("print-iframe")?.contentDocument ||
    hiddenRef.current?.contentDocument;

  return doc?.querySelector(".invoice-box");
};
 const [waMenuOpen, setWaMenuOpen] = useState(false);
    useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handlePrint = () => {
    const iframe = document.getElementById('print-iframe');
    if (iframe) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const handleWhatsAppClick = () => {
  setWaMenuOpen((prev) => !prev);
};

const shareAsImage = async () => {
  setWaMenuOpen(false);
  const captureElement = getCaptureElement();
  if (!captureElement) return alert('Content not ready.');

  const countryCode = '91';
  const mobileNumber = order?.MobileNo?.replace(/[^0-9]/g, '') || '';
  if (!mobileNumber) return alert('Mobile number not available.');

  const fullNumber = countryCode + mobileNumber;

  try {
    await waitForImages(captureElement);
    const canvas = await html2canvas(captureElement, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",

    width: captureElement.scrollWidth,
    height: captureElement.scrollHeight,

    windowWidth: captureElement.scrollWidth,
    windowHeight: captureElement.scrollHeight,

    scrollX: 0,
    scrollY: 0
});
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    const copied = await copyImageToClipboard(blob);
    if (copied) {
      alert('Image copied! Paste in WhatsApp.');
      window.open(`https://wa.me/${fullNumber}`, '_blank');
      return;
    }

    const shared = await shareImageFile(blob);
    if (!shared) {
      alert('Image sending is blocked by browser security. Host the IIS site with HTTPS to send the image like localhost.');
    }
  } catch (err) {
    console.warn(err);
  }
};

const shareAsPDF = async () => {
  setWaMenuOpen(false);
  const captureElement = getCaptureElement();
  if (!captureElement) return alert('Content not ready.');

  try {
    await waitForImages(captureElement);
    const canvas = await html2canvas(captureElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      scrollY: -window.scrollY, // ensure full capture
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    // ✅ Add new pages for remaining content
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    const pdfBlob = pdf.output('blob');
    const file = new File([pdfBlob], 'invoice.pdf', { type: 'application/pdf' });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: 'Invoice PDF',
        text: 'Please find attached invoice PDF',
      });
    } else {
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    }
  } catch (err) {
    console.error('PDF generation failed:', err);
  }
};



  return (
    <>
      <iframe
        ref={hiddenRef}
        srcDoc={printableHtml}
        title="WhatsApp Capture"
        style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: '800px', height: '1200px', border: 'none', backgroundColor: 'white' }}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
        <div className="relative w-full max-w-2xl p-4 bg-white rounded-lg shadow-2xl">
          <h3 className="mb-2 text-lg font-semibold text-center">Preview</h3>
          <div className="border border-gray-300">
            <iframe
              id="print-iframe"
              srcDoc={printableHtml}
              title="Print Preview"
              style={{ width: '100%', height: '60vh', border: 'none' }}
            />
          </div>

           <div className="flex justify-end mt-2 gap-7">
          {/* Close Button */}
          <Button
            onClick={onClose}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
            bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md 
            hover:from-red-600 hover:to-red-700 hover:shadow-lg 
            transition-all duration-300 ease-in-out transform hover:scale-105"
          >
            <XCircle size={16} />
            Close
          </Button>

            {/* Print Button */}
            <Button
              onClick={handlePrint}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white
               bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md
               hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg
               transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <Printer size={16} />
              Print
            </Button>
            
<div className="relative">
  <Button
    onClick={handleWhatsAppClick}
    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
      bg-green-500 rounded-lg shadow-md hover:bg-green-600 transition-all duration-300"
  >
    <Send size={16} /> WhatsApp
  </Button>

  {waMenuOpen && (
    <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border rounded-lg shadow-lg w-44">
      <button
        onClick={shareAsImage}
        className="flex items-center w-full gap-2 px-3 py-2 text-sm font-medium text-white transition-all duration-200 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600"
      >
        <img src="/icons/image-icon.svg" alt="image" className="w-4 h-4" />
        Share as Image
      </button>
      <button
        onClick={shareAsPDF}
        className="flex items-center w-full gap-2 px-3 py-2 mt-1 text-sm font-medium text-white transition-all duration-200 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600"
      >
        <img src="/icons/pdf-icon.svg" alt="pdf" className="w-4 h-4" />
        Share as PDF
      </button>
    </div>
  )}
</div>

          </div>
        </div>
      </div>
    </>
  );
};

const GridIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

const ListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="8" y1="6" x2="21" y2="6"></line>
    <line x1="8" y1="12" x2="21" y2="12"></line>
    <line x1="8" y1="18" x2="21" y2="18"></line>
    <line x1="3" y1="6" x2="3.01" y2="6"></line>
    <line x1="3" y1="12" x2="3.01" y2="12"></line>
    <line x1="3" y1="18" x2="3.01" y2="18"></line>
  </svg>
);
const OrderCard = ({
  order,
  onEdit,
  MakeReceipt,
    MakeInvoice,  
  onPrintInvoice,
  onPackingSlip,
  onQuotation,
  receiptDisabled,
  orderStatuses,
  handlePending,
  handleJobto,
  handleMakeInvoice, 
  handlePrintInvoice
}) => (
<div
  className={`p-4 transition border border-gray-200 shadow-sm rounded-xl hover:shadow-md ${
    order.InvNo && order.InvNo !== "InvNo :" ? "bg-yellow-100" : "bg-white"
  }`}
>

    <div className="mb-3">
      <h3 className="text-sm font-semibold text-gray-900">{order.Vname}</h3>
      <p className="text-xs text-gray-500">#{order.Poid}</p>
      <p className="text-xs text-gray-500">
        {order.DeliveryDate
          ? new Date(order.DeliveryDate).toLocaleDateString()
          : ""}
      </p>
    </div>

    {order.InvNo && (
      <p className="text-sm font-medium text-gray-700">{order.InvNo}</p>
    )}
    <p className="text-sm text-gray-600">
      {order.SumQty} / {order.Rate} &nbsp; &nbsp; Rs:{order.OrderTotal}
    </p>
    <p className="mb-3 text-sm text-gray-600">
      Job: {order.JobToName || "Jobto"}
    </p>

    <div className="grid grid-cols-2 gap-2">
      <Button
        className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-1 text-xs font-medium
        text-white
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg
             transition-all duration-300 ease-in-out"
        onClick={onEdit}
      >
        <FileEdit className="w-6 h-6" />
        Edit
      </Button>
<Button
  className={`w-full px-3 py-1.5 text-xs font-medium rounded-md ${
    order.ReceiptAmount > 0
      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
      : "bg-blue-500 text-white hover:bg-blue-600"
  }`}
  onClick={MakeReceipt}
  disabled={order.ReceiptAmount > 0}
>
  {order.ReceiptAmount > 0 ? "Receipt Made" : "Make Receipt"}
</Button>

    <Button
  className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-md hover:bg-green-600"
  onClick={() => onQuotation(order)}
>
  Quotation
</Button>


{/* Invoice Button */}
<Button
  className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-md hover:bg-green-600"
  onClick={() => {
    if (!order.InvNo || order.InvNo === "InvNo :") {
       if (window.confirm("Invoice not generated yet. Are you really want to generate Invoice?")) {
        handleMakeInvoice(order);
      }
    } else {
      handlePrintInvoice(order);
    }
  }}
>
  {order.InvNo && order.InvNo !== "InvNo :" 
    ? order.InvNo 
    : "Generate Invoice"}
</Button>


      <Button
        className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-md hover:bg-green-600"
        onClick={() => onPackingSlip(order)}
      >
        Packing Slip
      </Button>

      <Button
        key={order.Poid}
        onClick={() => handlePending(order)}
        className="w-full px-3 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600"
      >
        {orderStatuses[order.Poid] || order.Status}
      </Button>

      <Button
        onClick={() => handleJobto(order.Poid)}
        className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-md hover:bg-green-600"
      >
        Job: {order.JobToName ? order.JobToName : "Jobto"}
      </Button>
    </div>
  </div>
);

const SalesOrderList = ({ setView, setEditingContext, dateWise = false }) => {
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0];
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [disabledReceipts, setDisabledReceipts] = useState(new Set());
  const [disabledInvoices, setDisabledInvoices] = useState(new Set());
  const [printContent, setPrintContent] = useState('');
  const [currentOrder, setCurrentOrder] = useState(null);
  const [viewMode, setViewMode] = useState("card");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    raised: false,
    pending: false,
    packed: false,
    notPacked: false,
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeFilterType, setActiveFilterType] = useState(null);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const loadOrders = useCallback(() =>
    dateWise
      ? api.getSimplePOListTaxAPIDateWise(user.UsrName, fromDate, toDate)
      : api.getSimplePOListTaxAPI(user.UsrName), [dateWise, fromDate, toDate, user.UsrName]);

  
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOrders(orders);
    } else {
      const lower = searchTerm.toLowerCase();
      setFilteredOrders(
        orders.filter(
          (o) =>
            (o.Vname && o.Vname.toLowerCase().includes(lower)) ||
            (o.MobileNo && o.MobileNo.toLowerCase().includes(lower)) ||
            (o.InvNo && o.InvNo.toLowerCase().includes(lower)) ||
            (o.Poid && String(o.Poid).toLowerCase().includes(lower))
        )
      );
    }
  }, [searchTerm, orders]);

  useEffect(() => {
    localStorage.removeItem('disabledReceipts');
    localStorage.removeItem('disabledInvoices');
    setDisabledReceipts(new Set());
    setDisabledInvoices(new Set());
  }, []);

  const fetchOrders = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      setError("");
      try {
        const data = await loadOrders();
        let list = (data?.Table || []).map((o) => ({
          ...o,
          cpid: Number(o.cpid) || 0,
          ReceiptAmount: Number(o.ReceiptAmount) || 0, 
        }));

        list = list.filter(
          (obj, index, self) =>
            index === self.findIndex(
              (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
            )
        );

        setOrders(list);
        setFilteredOrders(list);

        const updates = {};
        list.forEach((o) => {
          updates[o.Poid] = o.Status || "Pending";
        });
        setOrderStatuses(updates);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to fetch orders.");
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [loadOrders]
  );

  useEffect(() => {
    let mounted = true;
    const loadInitialOrders = async () => {
      setLoading(true);
      try {
        await fetchOrders(false);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }

    };
    loadInitialOrders();
    return () => {
      mounted = false;
    };
  }, [fetchOrders]);

const handleMakeReceipt = async (order) => {
  if (order.ReceiptAmount > 0) return; // Already made

  try {
    await api.saveMakeReceipt({ Poid: order.Poid });
    alert('Receipt successfully generated!');

    // Update ReceiptAmount locally to disable button
    const updateOrder = (o) =>
      o.Poid === order.Poid ? { ...o, ReceiptAmount: 1 } : o;
    setOrders((prev) => prev.map(updateOrder));
    setFilteredOrders((prev) => prev.map(updateOrder));
  } catch (err) {
    console.error(err);
    alert('Failed to generate receipt.');
  }
};

   const handleMakeInvoice = async (order) => {
    if (disabledInvoices.has(order.Poid) || (order.InvNo && order.InvNo !== "InvNo :" && Number(order.cpid) > 0)) {
      return handlePrintInvoice(order);
    }

    const updated = new Set(disabledInvoices).add(order.Poid);
    setDisabledInvoices(updated);
    localStorage.setItem('disabledInvoices', JSON.stringify([...updated]));

    try {
      const resp = await api.MobGetfinalizeOrder(order.Poid);
      const maybeCpid = resp?.Table4?.[0]?.Cpid ?? resp?.data?.Table4?.[0]?.Cpid ?? resp?.MobGetfinalizeOrderjs?.Table4?.[0]?.Cpid ?? null;
      const newCpid = Number(maybeCpid) || 0;

      if (newCpid > 0) {
        const updateCpid = (o) => (o.Poid === order.Poid ? { ...o, cpid: newCpid } : o);
        setOrders((prev) => prev.map(updateCpid));
        setFilteredOrders((prev) => prev.map(updateCpid));

        await handlePrintInvoice(order);
        await fetchOrders(false);
        alert('Invoice successfully generated!');
      } else {
        alert('Invoice API returned no Cpid.');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to generate invoice.');
    }
  };

  const handlePrintInvoice = async (order) => {
    try {
      const resp = await api.MobGetfinalizeOrder(order.Poid);
      const content = resp?.Table?.[0]?.PrintContent ?? resp?.Table3?.[0]?.PrintContent ?? resp?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ?? resp?.data?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ?? null;

      if (content) {
        setPrintContent(content);
        setCurrentOrder(order);

        const match = content.match(/Bill No -\s*([^*<]+)/i);
        const newInvNo = match ? match[1].trim() : null;

        if (newInvNo && (!order.InvNo || order.InvNo === "InvNo :")) {
          const updateInvNo = (o) => (o.Poid === order.Poid ? { ...o, InvNo: newInvNo } : o);
          setOrders((prev) => prev.map(updateInvNo));
          setFilteredOrders((prev) => prev.map(updateInvNo));
        }
      } else {
        alert('Print content not found.');
      }
    } catch (err) {
      console.error(err);
      alert('Error fetching print content.');
    }
  };
  
  const [orderStatuses, setOrderStatuses] = useState({});

  const handlePending = async (order) => {
    console.log("handlePending called with:", order);

    try {
      const response = await api.GetPoOrderItemlistAPI(order.Poid, user.UsrName);
      const statusData = response?.Table?.[0] ?? null;

      if (statusData) {
        localStorage.setItem("poidContent", order.Poid);
        localStorage.setItem("username", user.UsrName);

        setView("pending-Salesview");

        setOrderStatuses((prev) => ({
          ...prev,
          [order.Poid]: statusData.Status,
        }));
      } else {
        alert("No status data found.");
      }
    } catch (err) {
      console.error("Error fetching order item list:", err);
      alert("Failed to fetch order items.");
    }
  };

  const handleJobto = async (poid) => {
    try {
      localStorage.setItem("poidContent", poid);

      const resp = await api.GetRmpUserAPI(user.UsrName);
      console.log("GetRmpUserAPI resp:", resp);

      setView("AllotSalesOrderToPerson");
    } catch (err) {
      console.error(err);
      alert("Error fetching job allocation data.");
    }
  };


  const handlePackingSlip = async (order) => {
    try {
      const resp = await api.get(`GetHtmlPackingSlipAPI?PoId=${order.Poid}`);
      console.log('Packing Slip API Response:', resp);

      const content = resp?.Table?.[0]?.PrintPackingSlip ?? null;

      if (content) {
        setPrintContent(content);
        setCurrentOrder(order);
      } else {
        alert('Packing slip content not found.');
        console.warn('Packing slip content could not be found in API response:', resp);
      }
    } catch (err) {
      console.error('Error fetching packing slip:', err);
      alert('Failed to fetch packing slip.');
    }
  };

 const handleQuotation = async (order) => {
  try {
    const resp = await api.get(`GetHtmlPoQuotationAPIRev?PoId=${order.Poid}`);
    console.log("Quotation API Response:", resp);

    // resp is already { Table: [...] }
    const content = resp?.Table?.[0]?.PoQuotationHtml ?? null;

    if (content) {
      setPrintContent(content);
      setCurrentOrder(order);
    } else {
      alert("Quotation content not found.");
    }
  } catch (err) {
    console.error("Error fetching quotation:", err);
    alert("Failed to fetch quotation.");
  }
};



  return (
    <div className={dateWise ? "p-0" : "p-3"}>
      {printContent && currentOrder && (
        <PrintPreview
          htmlContent={printContent}
          onClose={() => {
            setPrintContent("");
            setCurrentOrder(null);
          }}
          order={currentOrder}
        />
      )}
      {isFilterOpen && activeFilterType === "packing" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="p-6 bg-white rounded-lg shadow-lg w-80">
            <h3 className="mb-4 text-lg font-semibold">Filter by Packing</h3>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterOptions.packed}
                  onChange={(e) =>
                    setFilterOptions((prev) => ({ ...prev, packed: e.target.checked }))
                  }
                />
                Packed
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterOptions.notPacked}
                  onChange={(e) =>
                    setFilterOptions((prev) => ({ ...prev, notPacked: e.target.checked }))
                  }
                />
                Pending
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button onClick={() => setIsFilterOpen(false)} className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400">
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  setIsFilterOpen(false);
                  try {
                    const data = await loadOrders();
                    let list = (data?.Table || []).map((o) => ({ ...o, cpid: Number(o.cpid) || 0 }));

                    // Deduplicate
                    list = list.filter(
                      (obj, index, self) =>
                        index === self.findIndex((t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo)
                    );

                    if (filterOptions.packed && !filterOptions.notPacked) {
                      list = list.filter((o) => o.Status === "Packed");
                    } else if (!filterOptions.packed && filterOptions.notPacked) {
                      list = list.filter((o) => o.Status === "Pending");
                    }

                    setFilterOptions((prev) => ({ ...prev, raised: false, pending: false }));
                    setOrders(list);
                    setFilteredOrders(list);
                  } catch (err) {
                    console.error("Packing filter error:", err);
                    alert("Failed to filter orders.");
                  }
                }}
                className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      {isFilterOpen && activeFilterType === "invoice" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="p-6 bg-white rounded-lg shadow-lg w-80">
            <h3 className="mb-4 text-lg font-semibold">Filter by Invoice</h3>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterOptions.raised}
                  onChange={(e) =>
                    setFilterOptions((prev) => ({ ...prev, raised: e.target.checked }))
                  }
                />
                Raised
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterOptions.pending}
                  onChange={(e) =>
                    setFilterOptions((prev) => ({
                      ...prev,
                      pending: e.target.checked,
                    }))
                  }
                />
                Pending
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button
                onClick={() => setIsFilterOpen(false)}
                className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  setIsFilterOpen(false);
                  try {
                    const data = await loadOrders();
                    let list = (data?.Table || []).map((o) => ({
                      ...o,
                      cpid: Number(o.cpid) || 0,
                    }));

                    list = list.filter(
                      (obj, index, self) =>
                        index ===
                        self.findIndex(
                          (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
                        )
                    );

                    if (filterOptions.raised && !filterOptions.pending) {
                      list = list.filter((o) => o.InvNo && o.InvNo !== "InvNo :");
                    } else if (!filterOptions.raised && filterOptions.pending) {
                      list = list.filter((o) => !o.InvNo || o.InvNo === "InvNo :");
                    }

                    setFilterOptions((prev) => ({ ...prev, packed: false, notPacked: false }));

                    setOrders(list);
                    setFilteredOrders(list);
                  } catch (err) {
                    console.error("Invoice filter error:", err);
                    alert("Failed to filter orders.");
                  }
                }}
                className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      {
        isFilterOpen && activeFilterType === "jobto" && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="p-6 bg-white rounded-lg shadow-lg w-80">
              <h3 className="mb-4 text-lg font-semibold">Filter by JobTo</h3>

              <div className="flex flex-col gap-2 overflow-y-auto max-h-60">

                {[...new Set(orders.map((o) => o.JobToName).filter(Boolean))].map(
                  (job) => (
                    <label key={job} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={filterOptions[job] || false}
                        onChange={(e) =>
                          setFilterOptions((prev) => ({
                            ...prev,
                            [job]: e.target.checked,
                          }))
                        }
                      />
                      {job}
                    </label>
                  )
                )}
              </div>

              {isFilterOpen && activeFilterType === "jobto" && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="p-6 bg-white rounded-lg shadow-lg w-80">
                    <h3 className="mb-4 text-lg font-semibold">Filter by JobTo</h3>

                    <div className="flex flex-col gap-2 overflow-y-auto max-h-60">
                      {Object.values(
                        orders.reduce((acc, o) => {
                          const raw = o.JobToName?.trim() || "";
                          const display = raw === "" ? "(Blank)" : o.JobToName.trim();
                          const key = display.toLowerCase();

                          if (!acc[key]) acc[key] = display;
                          return acc;
                        }, {})
                      ).map((job) => (
                        <label key={job.toLowerCase()} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={filterOptions[job.toLowerCase()] || false}
                            onChange={(e) =>
                              setFilterOptions((prev) => ({
                                ...prev,
                                [job.toLowerCase()]: e.target.checked,
                              }))
                            }
                          />
                          {job}
                        </label>
                      ))}
                    </div>

                    <div className="flex justify-end gap-3 mt-4">
                      <Button
                        onClick={() => setIsFilterOpen(false)}
                        className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={async () => {
                          setIsFilterOpen(false);
                          try {
                            const data = await loadOrders();
                            let list = (data?.Table || []).map((o) => ({
                              ...o,
                              cpid: Number(o.cpid) || 0,
                            }));

                            list = list.filter(
                              (obj, index, self) =>
                                index ===
                                self.findIndex(
                                  (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
                                )
                            );

                            const selectedJobs = Object.keys(filterOptions).filter(
                              (k) => filterOptions[k]
                            );
                            if (selectedJobs.length > 0) {
                              list = list.filter((o) => {
                                const normalized = o.JobToName?.trim() || "";
                                const jobKey =
                                  normalized === "" ? "(blank)" : normalized.toLowerCase();
                                return selectedJobs.includes(jobKey);
                              });
                            }

                            setOrders(list);
                            setFilteredOrders(list);
                          } catch (err) {
                            console.error("JobTo filter error:", err);
                            alert("Failed to filter orders.");
                          }
                        }}
                        className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
                      >
                        OK
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-4">
                <Button
                  onClick={() => setIsFilterOpen(false)}
                  className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    setIsFilterOpen(false);
                    try {
                      const data = await loadOrders();
                      let list = (data?.Table || []).map((o) => ({
                        ...o,
                        cpid: Number(o.cpid) || 0,
                      }));

                      list = list.filter(
                        (obj, index, self) =>
                          index ===
                          self.findIndex(
                            (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
                          )
                      );

                      const selectedJobs = Object.keys(filterOptions).filter(
                        (k) => filterOptions[k]
                      );
                      if (selectedJobs.length > 0) {
                        list = list.filter((o) =>
                          selectedJobs.includes(o.JobToName?.toLowerCase().trim())
                        );
                      }

                      setOrders(list);
                      setFilteredOrders(list);
                    } catch (err) {
                      console.error("JobTo filter error:", err);
                      alert("Failed to filter orders.");
                    }
                  }}
                  className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        )}
      {dateWise ? (
        <div className="relative mb-2 flex flex-col gap-2 sm:min-h-[48px] sm:justify-end">
          <h1 className="text-center text-xl font-semibold tracking-normal text-slate-800 sm:text-2xl">
            Datewise Sales Orders
          </h1>
          <div className="flex items-end justify-end gap-2 sm:absolute sm:right-0 sm:top-0">
            <div>
              <label className="block text-xs font-medium text-slate-500">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-36 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-36 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700"
              />
            </div>
          </div>
        </div>
      ) : (
        <Heading>Sales Orders</Heading>
      )}

      <div className="flex flex-col items-center gap-3 mb-2 md:flex-row md:justify-between">

        <Button
          className="flex items-center gap-2 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-teal-700"
          onClick={() => setView("add-salesorder")}
        >
          <PlusCircle size={18} />
          <span className="font-medium">New</span>
        </Button>


        <div className="flex items-center justify-center rounded-lg bg-slate-100 p-1">
          <button
            onClick={() => setViewMode("card")}
            className={`rounded-md p-2 ${viewMode === "card"
              ? "bg-white text-teal-700 shadow-sm"
              : "text-slate-500 hover:bg-slate-50"
              }`}
          >
            <GridIcon />
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`rounded-md p-2 ${viewMode === "table"
              ? "bg-white text-teal-700 shadow-sm"
              : "text-slate-500 hover:bg-slate-50"
              }`}
          >
            <ListIcon />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-2 py-1.5">
        <Button
          onClick={() => setView("home")}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200"
        >
          ⬅
        </Button>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by vendor or mobile..."
        />
        <div className="relative">

          <Button
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-600 transition hover:bg-slate-200"
          >
            <span className="text-lg">🔽</span>
          </Button>

          {isDropdownOpen && (
            <div
              className={`absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50 transition-all duration-200 ease-out ${isDropdownOpen
                ? "opacity-100 scale-100"
                : "opacity-0 scale-95 pointer-events-none"
                }`}
            >
              <button
                className="flex items-center w-full gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-xl"
                onClick={() => {
                  setActiveFilterType("invoice");
                  setIsDropdownOpen(false);
                  setIsFilterOpen(true);
                }}
              >
                <span className="text-base">📑</span>
                <span>Filter by Invoice</span>
              </button>

              <button
                className="flex items-center w-full gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setActiveFilterType("packing");
                  setIsDropdownOpen(false);
                  setIsFilterOpen(true);
                }}
              >
                <span className="text-base">📦</span>
                <span>Filter by Packing</span>
              </button>

              <button
                className="flex items-center w-full gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 last:rounded-b-xl"
                onClick={() => {
                  setActiveFilterType("jobto");
                  setIsDropdownOpen(false);
                  setIsFilterOpen(true);
                }}
              >
                <span className="text-base">🛠</span>
                <span>Filter by JobTo</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {
        loading ? (
          <Spinner />
        ) : error ? (
          <MessageBox message={error} />
        ) : filteredOrders.length === 0 ? (
          <div className="py-12 text-center text-gray-500">No orders found.</div>
        ) : viewMode === "card" ? (

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.Poid}
                order={order}
                onEdit={() => {
                  setEditingContext({
                    id: order.Poid,
                    customerId: order.Vid,
                    orderDate: order.Podate,
                  });
                  setView("edit-salesorder");
                }}
                MakeReceipt={() => handleMakeReceipt(order)}
                 MakeInvoice={() => handleMakeInvoice(order)} 
                onPrintInvoice={() => handlePrintInvoice(order)}
                onPackingSlip={() => handlePackingSlip(order)}
                onQuotation={() => handleQuotation(order)}
                receiptDisabled={disabledReceipts.has(order.Poid)}
                  handleMakeInvoice={handleMakeInvoice} // ✅ pass it
                  handlePrintInvoice={handlePrintInvoice}
                invoiceDisabled={
                  disabledInvoices.has(order.Poid) || Number(order.cpid) > 0
                }
                orderStatuses={orderStatuses}
                handlePending={handlePending}
                handleJobto={handleJobto}
              />
            ))}
          </div>
        ) : (
          // ✅ Table View
          <div className="mt-4 overflow-x-auto bg-white border border-gray-200 shadow-md rounded-xl">
            <table className="w-full text-sm text-left">
              <thead className="tracking-wider text-gray-900 uppercase bg-gray-100">
                <tr>
                  <th className="p-3">PO ID</th>
                  <th className="p-3">Vendor</th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Invoice</th>
                  <th className="p-3">Total</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.map((order) => (
      <tr
  key={order.Poid}
  className={`${
    order.InvNo && order.InvNo !== "InvNo :"
      ? "bg-yellow-100 hover:bg-yellow-200"
      : "bg-white hover:bg-gray-50"
  }`}
>
                    <td className="p-3">#{order.Poid}</td>
                    <td className="p-3">{order.Vname}</td>
                    <td className="p-3">
                      {order.DeliveryDate
                        ? new Date(order.DeliveryDate).toLocaleDateString()
                        : ""}
                    </td>
                    <td className="p-3">{order.InvNo || "-"}</td>
                    <td className="p-3">{order.OrderTotal}</td>
                    <td className="p-3 text-center">
                      <div className="flex flex-wrap justify-center gap-2">
                        <Button
                          className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-0 text-xs font-medium text-white
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg
             transition-all duration-300 ease-in-out"
                          onClick={() => {
                            setEditingContext({
                              id: order.Poid,
                              customerId: order.Vid,
                              orderDate: order.Podate,
                            });
                            setView("edit-salesorder");
                          }}
                        >
                          <FileEdit className="w-5 h-5" />
                          Edit
                        </Button>

                <Button
  className={`min-w-[70px] px-2 py-1 rounded ${
    order.ReceiptAmount > 0
      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
      : "bg-blue-500 text-white hover:bg-blue-600"
  }`}
  onClick={() => handleMakeReceipt(order)}
  disabled={order.ReceiptAmount > 0}
>
  {order.ReceiptAmount > 0 ? "Receipt Made" : "Receipt"}
</Button>

 {/* Invoice Button */}
<Button
  className="min-w-[120px] px-2 py-1 text-white bg-green-500 rounded hover:bg-green-600"
  onClick={() => {
    if (!order.InvNo || order.InvNo === "InvNo :") {
       if (window.confirm("Invoice not generated yet. Are you really want to generate Invoice?")) {
        handleMakeInvoice(order);
      }
    } else {
      handlePrintInvoice(order);
    }
  }}
>
  {order.InvNo && order.InvNo !== "InvNo :" 
    ? order.InvNo 
    : "Generate Invoice"}
</Button>

<Button
  className="min-w-[80px] px-2 py-1 text-white bg-green-500 rounded hover:bg-green-600"
  onClick={() => handleQuotation(order)}
>
  Quotation
</Button>

                        <Button
                          className="min-w-[70px] px-2 py-1 text-white bg-green-500 rounded hover:bg-green-600"
                          onClick={() => handlePackingSlip(order)}
                        >
                          Packing
                        </Button>

                        <Button
                          className="min-w-[70px] px-2 py-1 text-white bg-green-500 rounded hover:bg-green-600"
                           onClick={() => handlePending(order)}
                        >
                          {orderStatuses[order.Poid] || order.Status}
                        </Button>

                        <Button
                          className="min-w-[70px] px-2 py-1 text-white bg-green-500 rounded hover:bg-green-600"
                            onClick={() => handleJobto(order.Poid)}
                        >
                         Job: {order.JobToName ? order.JobToName : "Jobto"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div >
  );
};

export default SalesOrderList;


